/**
 * 目標の入力欄について「**何を出すか**」だけを決める（R-03 / R03-C-2）。
 *
 * ★このファイルは **DOM を触らない**（`account-review-view.ts` と同じ理由）。
 *   判断を描画関数の中に書くと、その判断を壊しても `tsc` もテストも落ちない。
 *
 * ## ★ここが担っている唯一の判断：「未設定」と「0」を分ける
 *
 * `daily_sales_targets.target_sales` は `NOT NULL DEFAULT 0`。
 * **行が無い（未設定）**と**0 を入れた日（定休日）**は、DB では別だが
 * 画面に「0」と出した瞬間に**同じに見える。**
 * 達成率（C-3）は目標0 のとき `null` を返す約束なので、
 * ★**入力する人が「まだ入れていない」と「0 と決めた」を区別できないと、
 *   永久に空欄のままの日が「目標0の日」に化ける。**
 */
import type { DailySalesTarget, MonthlyTarget } from "./api.js";

const WEEKDAY = ["日", "月", "火", "水", "木", "金", "土"] as const;

/** 未設定のときに欄の隣へ出す言葉。★0 のときは出さない */
export const UNSET_NOTE = "未設定";

export interface MonthlyTargetFieldView {
  key: string;
  label: string;
  unit: string;
  /** 入力欄の初期値。★未設定は空文字（`0` を置かない） */
  value: string;
  /** ★未設定の項目にだけ付く。0 の項目には付かない */
  note: string | null;
}

export function monthlyTargetFields(targets: MonthlyTarget[]): MonthlyTargetFieldView[] {
  return targets.map((t) => ({
    key: t.key,
    label: t.label,
    unit: t.unit,
    value: t.value ?? "",
    note: t.value === null ? UNSET_NOTE : null,
  }));
}

export interface DailyTargetRowView {
  businessDate: string;
  /** `01(月)` の形。★月は欄の外（見出し）に1度だけ出す */
  label: string;
  /** 日曜日。★休みとは限らない（営業日カレンダーを見ていない）ので色分けの根拠にしない */
  isSunday: boolean;
  salesValue: string;
  note: string | null;
}

export function dailyTargetRows(targets: DailySalesTarget[]): DailyTargetRowView[] {
  return targets.map((t) => {
    const day = new Date(`${t.businessDate}T00:00:00Z`);
    const weekday = WEEKDAY[day.getUTCDay()] ?? "";
    return {
      businessDate: t.businessDate,
      label: `${t.businessDate.slice(8, 10)}(${weekday})`,
      isSunday: day.getUTCDay() === 0,
      salesValue: t.targetSales ?? "",
      note: t.targetSales === null ? UNSET_NOTE : null,
    };
  });
}

/**
 * 見出しの下に出す1行。
 *
 * ★**「全部入っている」ときも黙らない。** 未設定が0件だと何も出さない作りにすると、
 *   「読み込めていない」と「全部入っている」が画面で同じになる
 *   （`.loop/memory.md`「未紐付け・空の結果に、黙って空を返さない」）。
 */
export function dailyTargetsSummary(rows: DailyTargetRowView[]): string {
  const unset = rows.filter((r) => r.note !== null).length;
  if (rows.length === 0) return "この月の日付が取得できませんでした";
  if (unset === 0) return `${rows.length}日ぶん、すべて設定済みです`;
  return `${rows.length}日のうち ${unset}日が${UNSET_NOTE}です（0円と入力した日は設定済みとして数えます）`;
}

/** 率のキー（利益目標・目標FL の3キーのうち 2 つ）。★サーバは 0〜1 の小数で持つ。画面は % で受ける */
const RATE_KEYS: readonly string[] = ["target_cost_rate", "target_labor_cost_rate"];

export interface ProfitTargetFieldView extends MonthlyTargetFieldView {
  isRate: boolean;
}

/** 利益目標・目標FL の入力欄。★率は 0〜1 を % へ直して初期値にする（0.315 → 31.5）。未設定は空文字 */
export function profitTargetFields(targets: MonthlyTarget[]): ProfitTargetFieldView[] {
  return targets.map((t) => {
    const isRate = RATE_KEYS.includes(t.key);
    return {
      key: t.key,
      label: t.label,
      unit: isRate ? "%" : t.unit,
      isRate,
      value: t.value === null ? "" : isRate ? String(Number((Number(t.value) * 100).toFixed(4))) : t.value,
      note: t.value === null ? UNSET_NOTE : null,
    };
  });
}

/**
 * 保存する本文。★空欄は送らない（触っていない項目を 0 で上書きしない）。
 * ★率は % の入力を 100 で割って 0〜1 にする（浮動小数点の尾を落とす）。桁・範囲の最後の砦はサーバ。
 */
export function profitTargetPayload(inputs: { key: string; raw: string }[]): { key: string; value: number }[] {
  return inputs
    .filter((i) => i.raw !== "")
    .map((i) => ({
      key: i.key,
      value: RATE_KEYS.includes(i.key) ? Number((Number(i.raw) / 100).toFixed(6)) : Number(i.raw),
    }));
}
